// src/pages/WebBookingSettingsPage.js
// ============================================================
// 線上訂位設定（Plan 090）— 餐廳自助設定線上訂位頁
//   啟用 · 訂位網址(自訂短代稱) · 封面圖 · 訂位限制(含Email) · 營業時段 · 加購選項 · 特殊需求標記
// 後端：/api/web-booking/config（JWT + adminOnly）。所有管理員可見（App.js AdminRoute）。
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';

import {
  Box, Paper, Typography, Switch, TextField, Button, IconButton, MenuItem,
  Stack, Snackbar, Alert, CircularProgress, FormControlLabel, Checkbox, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Layout from '../components/Layout';
import webBookingService from '../services/webBookingService';
import CompanyConstraintsSection from '../components/web-booking/CompanyConstraintsSection';

// 適用星期（T-059）：顯示順序一~日；值用 JS getDay()（0=日）
const DOW_ALL = [0, 1, 2, 3, 4, 5, 6];
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DOW_LABEL = ['日', '一', '二', '三', '四', '五', '六'];
// 全選→「每天」；連續三天以上縮寫成「週一～五」
const dowSummary = (ds) => {
  if (!ds || ds.length === 0) return '請至少選一天';
  if (ds.length === 7) return '每天';
  const inOrder = DOW_ORDER.filter((d) => ds.includes(d));
  const runs = []; let run = [inOrder[0]];
  for (let i = 1; i < inOrder.length; i += 1) {
    if (DOW_ORDER.indexOf(inOrder[i]) === DOW_ORDER.indexOf(inOrder[i - 1]) + 1) run.push(inOrder[i]);
    else { runs.push(run); run = [inOrder[i]]; }
  }
  runs.push(run);
  return runs.map((r) => (r.length >= 3
    ? `週${DOW_LABEL[r[0]]}～${DOW_LABEL[r[r.length - 1]]}`
    : r.map((d) => `週${DOW_LABEL[d]}`).join('、'))).join('、');
};

const GREEN = '#06C755';
const ADDON_TYPES = [
  { value: 'toggle', label: '開關（可勾選）' },
  { value: 'counter', label: '數量（＋/−）' },
  { value: 'text', label: '文字' },
  { value: 'single_select', label: '單選' },
  { value: 'multi_select', label: '多選' }
];
const SLOT_OPTIONS = [15, 30, 60];

const DEFAULTS = {
  enabled: false, slug: '', defaultSlug: '', coverImageUrl: '', coverImages: [],
  displayName: '', companyName: '',
  address: '', phone: '', website: '', mapUrl: '',
  maxPartySize: 10, bookingWindowDays: 90, slotMinutes: 30, minLeadMinutes: 0, maxActivePerPhone: 3,
  collectEmail: false, businessHours: [], reservationAddons: [], specialRequestTags: [],
  // 官方 LINE 先點餐（訂位成功頁標題 + 按鈕 + 說明；後端 sanitize）
  preorderLine: { enabled: false, title: '', url: '', description: '' },
  // 外帶點餐（Plan 106；後端 getTakeoutConfig 會回完整形狀）
  takeout: {
    enabled: false,
    bankTransferEnabled: true,
    bankInfo: { bankName: '', bankCode: '', accountNumber: '', accountName: '' },
    leadMinutes: 40, slotMinutes: 15, windowDays: 3, notice: ''
  }
};

const isSelectAddon = (t) => t === 'single_select' || t === 'multi_select';

// 加購 select 選項：編輯態用 {value(菜名), price(價格)}；儲存/後端用 {value, label('菜名 價格')}。
// 載入時把後端的 {value,label} 拆成 {value,price}（label 去掉 value 前綴＝price）。
const hydrateAddons = (addons) => (Array.isArray(addons) ? addons : []).map((a) => {
  if (!a || !isSelectAddon(a.type) || !Array.isArray(a.options)) return a;
  return {
    ...a,
    options: a.options.map((o) => {
      const value = String((o && (o.value || o.label)) || '');
      const label = String((o && o.label) || '');
      const price = (value && label && label.startsWith(value)) ? label.slice(value.length).trim() : '';
      return { value, price };
    })
  };
});
// 儲存時把 {value,price} 組回 {value,label}；丟棄空菜名。
const serializeAddons = (addons) => (Array.isArray(addons) ? addons : []).map((a) => {
  if (!a || !isSelectAddon(a.type) || !Array.isArray(a.options)) return a;
  return {
    ...a,
    options: a.options
      .map((o) => {
        const value = String((o && o.value) || '').trim();
        const price = String((o && o.price) || '').trim();
        return { value, label: price ? `${value} ${price}` : value };
      })
      .filter((o) => o.value)
  };
});

const SectionPaper = ({ title, action, children }) => (
  <Paper variant="outlined" sx={{ p: 3, mb: 2.5, borderRadius: 2 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{title}</Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const WebBookingSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [config, setConfig] = useState(DEFAULTS);
  const [original, setOriginal] = useState(DEFAULTS);
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | {available, reason}
  const [newTag, setNewTag] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [photoPicker, setPhotoPicker] = useState(false);   // 從菜單選圖 picker 開關
  const [pickerTab, setPickerTab] = useState('menu');       // 'menu' | 'url'
  const [menuPhotos, setMenuPhotos] = useState([]);         // 菜單照片庫 [{name,imageUrl}]
  const [menuLoading, setMenuLoading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const notify = (message, severity = 'success') => setSnack({ open: true, message, severity });
  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const setTakeout = (key, value) => setConfig((c) => ({ ...c, takeout: { ...c.takeout, [key]: value } }));
  const setPreorder = (key, value) => setConfig((c) => ({ ...c, preorderLine: { ...c.preorderLine, [key]: value } }));
  const setTakeoutBank = (key, value) => setConfig((c) => ({
    ...c, takeout: { ...c.takeout, bankInfo: { ...c.takeout.bankInfo, [key]: value } }
  }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await webBookingService.getConfig();
      if (body && body.success && body.data) {
        const d = { ...DEFAULTS, ...body.data };
        d.slug = d.slug || '';
        d.coverImageUrl = d.coverImageUrl || '';
        d.coverImages = Array.isArray(d.coverImages) ? d.coverImages : (d.coverImageUrl ? [d.coverImageUrl] : []);
        d.reservationAddons = hydrateAddons(d.reservationAddons);
        setConfig(d);
        setOriginal(d);
      }
    } catch (e) {
      notify('載入設定失敗', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 自訂網址即時檢查（debounce 500ms；等於自家 companies.slug 視為可用）
  useEffect(() => {
    const s = (config.slug || '').trim();
    if (!s) { setSlugStatus(null); return undefined; }
    if (s === config.defaultSlug) { setSlugStatus({ available: true }); return undefined; }
    setSlugStatus('checking');
    const id = setTimeout(async () => {
      try { setSlugStatus(await webBookingService.checkSlug(s)); }
      catch (e) { setSlugStatus(null); }
    }, 500);
    return () => clearTimeout(id);
  }, [config.slug, config.defaultSlug]);

  const publicUrl = `${window.location.origin}/r/${(config.slug || config.defaultSlug || '').trim()}`;

  // ---- 封面圖上傳 ----
  // ---- 餐廳照片相簿（coverImages；訂位頁頂部輪播，上限 5）----
  const MAX_COVERS = 5;
  const coverList = Array.isArray(config.coverImages) ? config.coverImages : [];
  const addCover = (url) => {
    const u = String(url || '').trim();
    if (!u) return;
    setConfig((c) => {
      const cur = Array.isArray(c.coverImages) ? c.coverImages : [];
      if (cur.includes(u) || cur.length >= MAX_COVERS) return c;
      return { ...c, coverImages: [...cur, u] };
    });
  };
  const removeCover = (i) => setConfig((c) => ({ ...c, coverImages: (c.coverImages || []).filter((_, j) => j !== i) }));
  const openPhotoPicker = async () => {
    setPickerTab('menu'); setUrlInput(''); setPhotoPicker(true);
    setMenuLoading(true);
    try { setMenuPhotos(await webBookingService.getMenuPhotos()); }
    catch (e) { setMenuPhotos([]); }
    finally { setMenuLoading(false); }
  };
  const pickFromMenu = (url) => { addCover(url); setPhotoPicker(false); };
  const addFromUrl = () => { const u = urlInput.trim(); if (u) { addCover(u); setUrlInput(''); setPhotoPicker(false); } };

  const onPickCover = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const url = await webBookingService.uploadCover(file);
      addCover(url);
      setPhotoPicker(false);
      notify('照片已上傳');
    } catch (err) {
      notify('上傳失敗，可改為直接貼圖片網址', 'error');
    } finally {
      setUploading(false);
    }
  };

  // ---- 營業時段 ----
  const addHour = () => setConfig((c) => ({
    ...c,
    businessHours: [...c.businessHours, { name: '', start: '17:00', end: '21:00', daysOfWeek: [0, 1, 2, 3, 4, 5, 6] }]
  }));
  const updateHour = (i, key, value) => setConfig((c) => {
    const bh = c.businessHours.slice(); bh[i] = { ...bh[i], [key]: value }; return { ...c, businessHours: bh };
  });
  const removeHour = (i) => setConfig((c) => ({ ...c, businessHours: c.businessHours.filter((_, j) => j !== i) }));

  // ---- 適用星期 / 輪次（T-059）----
  // daysOfWeek 未設 = 每天適用（既有設定沒有這個欄位）。UI 上一律顯示成「全選」。
  const dowsOf = (h) => (Array.isArray(h.daysOfWeek) && h.daysOfWeek.length ? h.daysOfWeek : DOW_ALL);
  const toggleDow = (i, d) => setConfig((c) => {
    const bh = c.businessHours.slice();
    const cur = dowsOf(bh[i]);
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b);
    bh[i] = { ...bh[i], daysOfWeek: next };
    return { ...c, businessHours: bh };
  });
  const setAllDows = (i, all) => updateHour(i, 'daysOfWeek', all ? DOW_ALL : []);
  const toggleSeating = (i) => setConfig((c) => {
    const bh = c.businessHours.slice();
    const on = !bh[i].seating;
    // 勾起來時，提示欄還空的話幫忙帶一句，店家可以改可以清空
    const notice = on && !String(bh[i].notice || '').trim()
      ? `本輪用餐至 ${bh[i].end || ''}，敬請配合`
      : bh[i].notice;
    bh[i] = { ...bh[i], seating: on, notice };
    return { ...c, businessHours: bh };
  });

  // 每列的錯誤訊息（null = 沒問題）。後端 sanitizeWindow 也會擋，這裡先擋是為了給即時回饋。
  const hourError = (h) => {
    if (!Array.isArray(h.daysOfWeek) || h.daysOfWeek.length === 0) return '請至少選一天';
    if (h.seating) {
      const toMin = (v) => { const m = /^(\d{1,2}):(\d{2})/.exec(String(v || '')); return m ? +m[1] * 60 + +m[2] : null; };
      const e = toMin(h.end); const lo = toMin(h.lastOrder); const st = toMin(h.start);
      if (e === null || st === null || e <= st) return '「這是一輪」時，結束時間必須晚於開始時間';
      // lastOrder === end 會讓佔用區間變成空的 → 同一張桌會被重複配，必須擋
      if (lo !== null && lo >= e) return '「這是一輪」時，最後收客必須早於結束時間';
    }
    return null;
  };
  const hoursBlocked = useMemo(
    () => config.businessHours.some((h) => hourError(h) !== null),
    [config.businessHours]
  );

  // ---- 加購選項 ----
  const addAddon = () => setConfig((c) => ({ ...c, reservationAddons: [...c.reservationAddons, { label: '', type: 'toggle' }] }));
  const updateAddon = (i, key, value) => setConfig((c) => {
    const a = c.reservationAddons.slice(); a[i] = { ...a[i], [key]: value }; return { ...c, reservationAddons: a };
  });
  const removeAddon = (i) => setConfig((c) => ({ ...c, reservationAddons: c.reservationAddons.filter((_, j) => j !== i) }));

  // ---- 加購「單選/多選」的選項（菜色）----
  const addOption = (i) => setConfig((c) => {
    const a = c.reservationAddons.slice();
    const opts = Array.isArray(a[i].options) ? a[i].options.slice() : [];
    opts.push({ value: '', price: '' });
    a[i] = { ...a[i], options: opts };
    return { ...c, reservationAddons: a };
  });
  const updateOption = (i, oi, key, value) => setConfig((c) => {
    const a = c.reservationAddons.slice();
    const opts = (a[i].options || []).slice();
    opts[oi] = { ...opts[oi], [key]: value };
    a[i] = { ...a[i], options: opts };
    return { ...c, reservationAddons: a };
  });
  const removeOption = (i, oi) => setConfig((c) => {
    const a = c.reservationAddons.slice();
    a[i] = { ...a[i], options: (a[i].options || []).filter((_, j) => j !== oi) };
    return { ...c, reservationAddons: a };
  });

  // ---- 特殊需求標記 ----
  const addTag = () => {
    const t = newTag.trim();
    if (t && !config.specialRequestTags.includes(t)) setField('specialRequestTags', [...config.specialRequestTags, t]);
    setNewTag('');
  };
  const removeTag = (i) => setField('specialRequestTags', config.specialRequestTags.filter((_, j) => j !== i));

  const slugBlocked = slugStatus && slugStatus !== 'checking' && slugStatus.available === false;

  // 有未儲存變更（供 sticky 儲存列的提示與按鈕狀態）
  const dirty = useMemo(() => JSON.stringify(config) !== JSON.stringify(original), [config, original]);

  // 銀行轉帳收款開關（關閉 → 隱藏銀行四欄，客人取餐時付款；缺省視為開啟）
  const takeoutTransferOn = config.takeout?.bankTransferEnabled !== false;

  const handleSave = async () => {
    if (slugBlocked) { notify('訂位網址不可使用，請修改', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        enabled: config.enabled,
        slug: (config.slug || '').trim(),
        displayName: (config.displayName || '').trim(),
        coverImageUrl: config.coverImageUrl || null,
        coverImages: coverList, // 相簿（後端存陣列 + 同步第一張到 coverImageUrl）
        address: config.address || null,
        phone: config.phone || null,
        website: config.website || null,
        mapUrl: config.mapUrl || null,
        maxPartySize: config.maxPartySize,
        bookingWindowDays: config.bookingWindowDays,
        slotMinutes: config.slotMinutes,
        minLeadMinutes: config.minLeadMinutes,
        maxActivePerPhone: config.maxActivePerPhone,
        collectEmail: config.collectEmail,
        businessHours: config.businessHours,
        reservationAddons: serializeAddons(config.reservationAddons),
        specialRequestTags: config.specialRequestTags,
        preorderLine: config.preorderLine, // 官方 LINE 先點餐（後端 sanitize url/description）
        takeout: config.takeout // 外帶點餐（Plan 106；後端 clamp/sanitize）
      };
      const body = await webBookingService.updateConfig(payload);
      if (body && body.success) {
        const d = { ...DEFAULTS, ...body.data, slug: body.data.slug || '', coverImageUrl: body.data.coverImageUrl || '' };
        d.coverImages = Array.isArray(d.coverImages) ? d.coverImages : (d.coverImageUrl ? [d.coverImageUrl] : []);
        d.reservationAddons = hydrateAddons(d.reservationAddons);
        setConfig(d); setOriginal(d);
        notify('設定已儲存');
      } else {
        notify((body && body.message) || '儲存失敗', 'error');
      }
    } catch (err) {
      notify(err?.response?.data?.message || '儲存失敗', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setConfig(original); setSlugStatus(null); };

  const numField = (label, key, helper) => (
    <TextField label={label} type="number" size="small" fullWidth value={config[key]}
      onChange={(e) => setField(key, e.target.value)} helperText={helper}
      InputProps={{ inputProps: { min: 0 } }} />
  );

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: '96px', sm: 3 }, maxWidth: 900, mx: 'auto' }}>
        {/* header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>線上訂位設定</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>設定線上訂位頁、網址、訂位限制、營業時段與加購選項。</Typography>
          </Box>
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Switch checked={config.enabled} onChange={(e) => setField('enabled', e.target.checked)}
              sx={{ '& .Mui-checked': { color: GREEN }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }} />}
            label="啟用線上訂位" labelPlacement="start" />
        </Stack>

        {/* 訂位網址 */}
        <SectionPaper title="訂位網址">
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1.5 }}>
            可自訂短代稱讓網址更好記（小寫字母開頭、3–32 字，限 a-z 0-9 _）。
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{window.location.origin}/r/</Typography>
            <TextField size="small" value={config.slug} placeholder={config.defaultSlug || 'your-slug'}
              onChange={(e) => setField('slug', e.target.value.toLowerCase())} sx={{ width: 200, '& input': { fontFamily: 'monospace' } }} />
            {slugStatus === 'checking' && <CircularProgress size={18} />}
            {slugStatus && slugStatus !== 'checking' && slugStatus.available && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: GREEN }}>
                <CheckCircleIcon sx={{ fontSize: 18 }} /><Typography sx={{ fontSize: 14 }}>可使用</Typography>
              </Stack>
            )}
            {slugBlocked && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'error.main' }}>
                <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: 14 }}>{slugStatus.reason === 'TAKEN' ? '已被使用' : '格式不符'}</Typography>
              </Stack>
            )}
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontFamily: 'monospace', color: 'primary.main' }}>{publicUrl}</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" startIcon={<OpenInNewIcon />}
                onClick={() => window.open(publicUrl, '_blank', 'noopener')}>預覽訂位頁</Button>
              <Button size="small" variant="outlined"
                onClick={() => { navigator.clipboard?.writeText(publicUrl); notify('已複製連結'); }}>複製連結</Button>
            </Stack>
          </Stack>
        </SectionPaper>

        {/* 餐廳照片相簿（訂位頁頂部輪播）*/}
        <SectionPaper title="餐廳照片">
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 2 }}>顯示於訂位頁最上方,可放 1–{MAX_COVERS} 張(門面／環境／招牌菜),客人可左右滑。建議橫向 16:9。</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {coverList.map((src, i) => (
              <Box key={i} sx={{ position: 'relative', width: 108, height: 76, borderRadius: 2, overflow: 'hidden', bgcolor: '#e5e7eb',
                backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid', borderColor: 'divider' }}>
                {i === 0 && <Box sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 10.5, fontWeight: 700, px: 0.7, py: '1px', borderRadius: 1 }}>封面</Box>}
                <IconButton size="small" onClick={() => removeCover(i)}
                  sx={{ position: 'absolute', top: 2, right: 2, width: 22, height: 22, bgcolor: 'rgba(0,0,0,.5)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.7)' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Box>
            ))}
            {coverList.length < MAX_COVERS && (
              <Box onClick={openPhotoPicker}
                sx={{ width: 108, height: 76, borderRadius: 2, border: '2px dashed', borderColor: '#c3d0de', bgcolor: '#f6f8fb', color: 'primary.main',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3, cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>
                <AddIcon fontSize="small" /> 新增照片
              </Box>
            )}
          </Box>
          {coverList.length === 0 && <Typography color="text.disabled" sx={{ fontSize: 13, mt: 1 }}>尚無照片 — 點「新增照片」從菜單挑或上傳</Typography>}
          <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 1.5 }}>第一張為封面(分享／分頁縮圖用)。最多 {MAX_COVERS} 張。</Typography>
        </SectionPaper>

        {/* 餐廳聯絡資訊（T-013）— 公開頁營業時間下方顯示 */}
        <SectionPaper title="餐廳聯絡資訊">
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1.5 }}>顯示於訂位頁「營業時間」下方（有填才顯示）。官網／Google Maps 會做成可點連結。</Typography>
          {/* 餐廳顯示名稱（106）：與公司名稱（法人名）脫鉤；空 = 用公司名稱 */}
          <TextField label="餐廳顯示名稱" size="small" fullWidth value={config.displayName || ''}
            onChange={(e) => setField('displayName', e.target.value)}
            placeholder={config.companyName || '例：紅舍私房菜'}
            helperText={`顯示於訂位/外帶頁與分頁標題；留空則用公司名稱「${config.companyName || '—'}」`}
            sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="餐廳地址" size="small" fullWidth value={config.address || ''} onChange={(e) => setField('address', e.target.value)} placeholder="台北市…" />
            <TextField label="電話" size="small" fullWidth value={config.phone || ''} onChange={(e) => setField('phone', e.target.value)} placeholder="02-xxxx-xxxx" />
            <TextField label="官方網站" size="small" fullWidth value={config.website || ''} onChange={(e) => setField('website', e.target.value)} placeholder="https://…" />
            <TextField label="Google Maps 連結" size="small" fullWidth value={config.mapUrl || ''} onChange={(e) => setField('mapUrl', e.target.value)} placeholder="https://maps.app.goo.gl/…" />
          </Box>
          <Typography color="text.disabled" sx={{ fontSize: 12, mt: 1 }}>官網／Google Maps 需以 http:// 或 https:// 開頭，否則不會儲存。</Typography>
        </SectionPaper>

        {/* 訂位限制 */}
        <SectionPaper title="訂位限制">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {numField('每筆最多人數', 'maxPartySize')}
            {numField('可提前訂位天數', 'bookingWindowDays')}
            <TextField label="時段間隔" select size="small" fullWidth value={config.slotMinutes}
              onChange={(e) => setField('slotMinutes', Number(e.target.value))}>
              {SLOT_OPTIONS.map((m) => <MenuItem key={m} value={m}>{m} 分鐘</MenuItem>)}
            </TextField>
            {numField('最早可訂（分鐘後）', 'minLeadMinutes')}
            {numField('每支電話最多有效訂位', 'maxActivePerPhone')}
          </Box>
          <FormControlLabel sx={{ mt: 2 }}
            control={<Checkbox checked={config.collectEmail} onChange={(e) => setField('collectEmail', e.target.checked)} sx={{ '&.Mui-checked': { color: GREEN } }} />}
            label="於訂位頁收集顧客 Email（選填欄位；預設關閉）" />
        </SectionPaper>

        {/* 營業時段 */}
        <SectionPaper title="營業時段" action={<Button size="small" startIcon={<AddIcon />} onClick={addHour}>新增時段</Button>}>
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1.5 }}>訂位頁的可選時段，依此時段 × 上方「時段間隔」自動產生。「最後收客」選填 — 訂位時段收在此時間（不生到打烊點，替客人留用餐時間）；留空則到結束時間。公開頁仍顯示真實營業時間。「適用星期」全選＝每天（等同原本行為）；勾「這是一輪」代表兩輪制，該時段的訂位會佔用到結束時間為止。</Typography>
          <Stack spacing={1.5}>
            {config.businessHours.length === 0 && <Typography color="text.disabled" sx={{ fontSize: 14 }}>尚未設定營業時段</Typography>}
            {config.businessHours.map((h, i) => (
              // 每個時段獨立灰底匡線框 + 分組標籤（原本一排欄位難理解）
              <Box key={i} sx={{ bgcolor: '#f6f8fb', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField size="small" label="時段名稱" placeholder="午餐" value={h.name || ''} onChange={(e) => updateHour(i, 'name', e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
                  <Tooltip title="刪除此時段"><IconButton size="small" color="error" onClick={() => removeHour(i)}><DeleteOutlineIcon /></IconButton></Tooltip>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 210px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>營業時間</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TextField size="small" type="time" value={h.start || ''} onChange={(e) => updateHour(i, 'start', e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
                      <Typography color="text.secondary">—</Typography>
                      <TextField size="small" type="time" value={h.end || ''} onChange={(e) => updateHour(i, 'end', e.target.value)} sx={{ flex: 1, minWidth: 0 }} />
                    </Stack>
                  </Box>
                  <Box sx={{ flex: '1 1 150px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>最後收客（選填）</Typography>
                    <TextField size="small" type="time" value={h.lastOrder || ''} onChange={(e) => updateHour(i, 'lastOrder', e.target.value)} fullWidth />
                  </Box>
                </Box>

                {/* 適用星期（T-059）— 全選＝每天，等同既有行為 */}
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>適用星期</Typography>
                    <Stack direction="row" spacing={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                      {DOW_ORDER.map((d) => {
                        const on = dowsOf(h).includes(d);
                        return (
                          <Box
                            key={d}
                            onClick={() => toggleDow(i, d)}
                            sx={{
                              width: 34, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              cursor: 'pointer', fontSize: 13, userSelect: 'none',
                              borderRight: d === 0 ? 'none' : '1px solid', borderColor: 'divider',
                              bgcolor: on ? 'primary.main' : 'transparent',
                              color: on ? '#fff' : 'text.secondary',
                              fontWeight: on ? 600 : 400,
                              '&:hover': { bgcolor: on ? 'primary.dark' : 'action.hover' }
                            }}
                          >{DOW_LABEL[d]}</Box>
                        );
                      })}
                    </Stack>
                    <Typography sx={{ fontSize: 12.5, color: hourError(h) ? 'error.main' : 'text.secondary', fontWeight: hourError(h) ? 600 : 400 }}>
                      {dowSummary(dowsOf(h))}
                    </Typography>
                    <Button size="small" sx={{ minWidth: 0, fontSize: 12.5 }} onClick={() => setAllDows(i, dowsOf(h).length !== 7)}>
                      {dowsOf(h).length === 7 ? '全部取消' : '設為每天'}
                    </Button>
                  </Stack>

                  {/* 輪次 + 給客人的提示（T-059） */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mt: 1 }}>
                    <FormControlLabel
                      sx={{ mt: 2.5, mr: 0, whiteSpace: 'nowrap' }}
                      control={<Checkbox size="small" checked={!!h.seating} onChange={() => toggleSeating(i)} />}
                      label={<Typography sx={{ fontSize: 13.5 }}>這是一輪</Typography>}
                    />
                    <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>給客人的提示（選填，60 字內）</Typography>
                      <TextField
                        size="small" fullWidth inputProps={{ maxLength: 60 }}
                        value={h.notice || ''}
                        onChange={(e) => updateHour(i, 'notice', e.target.value)}
                        placeholder={`例：本輪用餐至 ${h.end || '19:00'}，敬請配合`}
                      />
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.75, lineHeight: 1.6 }}>
                    {h.seating
                      ? `此時段的訂位一律佔用到結束時間 ${h.end || ''} 為止（不用「入座 + 90 分用餐 + 15 分緩衝」），兩輪才能共用同一張桌。`
                      : '未勾選 → 沿用現行規則：每筆訂位佔用「入座 + 90 分用餐 + 15 分緩衝」。'}
                  </Typography>
                  {hourError(h) && (
                    <Typography sx={{ fontSize: 12.5, color: 'error.main', fontWeight: 600, mt: 0.75 }}>
                      ⚠ {hourError(h)}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </SectionPaper>

        {/* 特殊日期 / 公休（Plan 094 Phase 2）— 自我管理，立即存檔，不依賴下方「儲存」 */}
        <CompanyConstraintsSection />

        {/* 加購選項 */}
        <SectionPaper title="加購選項" action={<Button size="small" startIcon={<AddIcon />} onClick={addAddon}>新增加購</Button>}>
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1.5 }}>顯示於訂位頁「加購」區，由您自由增減（如：烤鴨、兒童椅）。</Typography>
          <Stack spacing={1.5}>
            {config.reservationAddons.length === 0 && <Typography color="text.disabled" sx={{ fontSize: 14 }}>尚無加購選項</Typography>}
            {config.reservationAddons.map((a, i) => (
              <Box key={i} sx={{ bgcolor: '#f6f8fb', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 1.5, sm: 2 } }}>
                {/* 手機直式：名稱佔整列，類型下拉＋刪除移到下一列（不再並排爆版）*/}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <TextField size="small" placeholder="名稱（如：烤鴨）" value={a.label || ''} onChange={(e) => updateAddon(i, 'label', e.target.value)}
                    sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }} />
                  <TextField size="small" select value={a.type || 'toggle'} onChange={(e) => updateAddon(i, 'type', e.target.value)}
                    sx={{ flex: { xs: '1 1 0', sm: '0 0 160px' }, minWidth: 0 }}>
                    {ADDON_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                  <Tooltip title="刪除"><IconButton size="small" color="error" onClick={() => removeAddon(i)}><DeleteOutlineIcon /></IconButton></Tooltip>
                </Box>
                {/* 需提前 N 天預訂：訂位日距今不足 N 天時，訂位頁該項灰掉不可選 */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1.2 }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>需提前預訂</Typography>
                  <TextField size="small" type="number" value={a.leadDays || 0}
                    onChange={(e) => updateAddon(i, 'leadDays', Math.max(0, Math.min(30, parseInt(e.target.value, 10) || 0)))}
                    inputProps={{ min: 0, max: 30 }} sx={{ width: 88 }} />
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>天（0＝當天可加；1＝需前一天訂）</Typography>
                </Box>
                {isSelectAddon(a.type) && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.secondary', mb: 1 }}>選項（菜色）— 客人可勾選</Typography>
                    <Stack spacing={1}>
                      {(a.options || []).length === 0 && <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>尚無選項，請按下方「新增選項」</Typography>}
                      {(a.options || []).map((o, oi) => (
                        // 手機:菜名佔整列，價格＋刪除下一列（避免菜名被壓成極窄）
                        <Box key={oi} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          <TextField size="small" placeholder="菜名（如：豬腳麵線）" value={o.value || ''} onChange={(e) => updateOption(i, oi, 'value', e.target.value)} sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: 0 }} />
                          <TextField size="small" placeholder="價格（選填）" value={o.price || ''} onChange={(e) => updateOption(i, oi, 'price', e.target.value)} sx={{ flex: { xs: '1 1 0', sm: '0 0 130px' }, minWidth: 0 }} />
                          <Tooltip title="刪除選項"><IconButton size="small" color="error" onClick={() => removeOption(i, oi)}><DeleteOutlineIcon /></IconButton></Tooltip>
                        </Box>
                      ))}
                    </Stack>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addOption(i)} sx={{ mt: 1 }}>新增選項</Button>
                    <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 1 }}>價格自由填（$980／時價／留空）。菜名為存值，顯示為「菜名 價格」。</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </SectionPaper>

        {/* 特殊需求標記 */}
        <SectionPaper title="特殊需求標記">
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 1.5 }}>顯示於訂位頁「特殊需求」區，客人可多選（如：靠窗、慶生、素食、嬰兒椅）。</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {config.specialRequestTags.length === 0 && <Typography color="text.disabled" sx={{ fontSize: 14 }}>尚無特殊需求標記</Typography>}
            {config.specialRequestTags.map((t, i) => (
              <Chip key={t} label={t} onDelete={() => removeTag(i)} />
            ))}
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="輸入標記後按新增（如：靠窗座位）" value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} sx={{ flex: 1 }} />
            <Button variant="contained" onClick={addTag}>新增</Button>
          </Stack>
        </SectionPaper>

        {/* 官方 LINE 先點餐（訂位成功頁 + 管理訂位頁的綠色按鈕）*/}
        <SectionPaper
          title="官方 LINE 先點餐"
          action={
            <FormControlLabel
              control={<Switch checked={!!config.preorderLine?.enabled} onChange={(e) => setPreorder('enabled', e.target.checked)}
                sx={{ '& .Mui-checked': { color: GREEN }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: GREEN } }} />}
              label={<Typography sx={{ fontSize: 14, fontWeight: 700 }}>啟用</Typography>} labelPlacement="start" />
          }>
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 2 }}>
            啟用後，客人「訂位成功」頁與「管理訂位」頁會出現綠色「加入官方帳號先點菜」按鈕，導向您的官方 LINE。說明文字只顯示在訂位成功頁。
          </Typography>
          <TextField label="官方 LINE 連結" size="small" fullWidth sx={{ mb: 2 }}
            value={config.preorderLine?.url || ''} onChange={(e) => setPreorder('url', e.target.value)}
            placeholder="https://lin.ee/xxxxxxx"
            helperText="LINE 官方帳號的加好友連結（需以 http:// 或 https:// 開頭）" />
          <TextField label="標題（訂位成功頁顯示，選填）" size="small" fullWidth sx={{ mb: 2 }}
            value={config.preorderLine?.title || ''} onChange={(e) => setPreorder('title', e.target.value)}
            placeholder="例：想先點餐嗎？"
            inputProps={{ maxLength: 40 }}
            helperText="說明文字上方的小標；留空則不顯示標題" />
          <TextField label="說明文字（訂位成功頁顯示，選填）" size="small" fullWidth multiline minRows={3}
            value={config.preorderLine?.description || ''} onChange={(e) => setPreorder('description', e.target.value)}
            placeholder="例：廚房僅老闆一人炒菜，沒預先點菜的顧客，用餐繁忙時段請耐心等待，謝謝"
            inputProps={{ maxLength: 300 }}
            helperText={`${(config.preorderLine?.description || '').length}/300`} />
        </SectionPaper>

        {/* 外帶點餐（Plan 106）*/}
        <SectionPaper
          title="外帶點餐"
          action={
            <FormControlLabel
              control={<Switch checked={!!config.takeout?.enabled} onChange={(e) => setTakeout('enabled', e.target.checked)} />}
              label={<Typography sx={{ fontSize: 14, fontWeight: 700 }}>啟用外帶</Typography>} labelPlacement="start" />
          }>
          <Typography color="text.secondary" sx={{ fontSize: 14, mb: 2 }}>
            啟用後，訂位頁（/r/{(config.slug || config.defaultSlug || '…')}）頂部會出現「內用訂位｜外帶自取」切換。
            菜單內容請至左側「菜單管理」維護；付款為銀行轉帳＋回填後五碼對帳（不串金流）。
          </Typography>
          {/* 銀行轉帳收款開關（關閉 → 隱藏銀行四欄，客人取餐時付款） */}
          <FormControlLabel sx={{ mb: 1 }}
            control={<Switch checked={config.takeout?.bankTransferEnabled !== false}
              onChange={(e) => setTakeout('bankTransferEnabled', e.target.checked)} />}
            label={<Typography sx={{ fontSize: 14, fontWeight: 700 }}>銀行轉帳收款（客人結帳回填後五碼）</Typography>} />
          {takeoutTransferOn ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField label="銀行名稱" size="small" value={config.takeout?.bankInfo?.bankName || ''}
                onChange={(e) => setTakeoutBank('bankName', e.target.value)} placeholder="國泰世華銀行" />
              <TextField label="銀行代碼" size="small" value={config.takeout?.bankInfo?.bankCode || ''}
                onChange={(e) => setTakeoutBank('bankCode', e.target.value)} placeholder="013" />
              <TextField label="匯款帳號" size="small" value={config.takeout?.bankInfo?.accountNumber || ''}
                onChange={(e) => setTakeoutBank('accountNumber', e.target.value)} placeholder="123-45-678901-2" />
              <TextField label="戶名" size="small" value={config.takeout?.bankInfo?.accountName || ''}
                onChange={(e) => setTakeoutBank('accountName', e.target.value)} />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>已關閉轉帳收款——客人結帳不需匯款，取餐時再付款。</Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <TextField label="最短備餐時間（分鐘）" size="small" type="number" value={config.takeout?.leadMinutes ?? 40}
              onChange={(e) => setTakeout('leadMinutes', e.target.value)}
              helperText="客人最快只能選「現在＋備餐時間」之後的取餐時段" />
            <TextField label="開放取餐天數（今天起 N 天）" size="small" type="number" value={config.takeout?.windowDays ?? 3}
              onChange={(e) => setTakeout('windowDays', e.target.value)}
              helperText="配合「需預訂」品項，預設 3 天（今天/明天/後天）" />
          </Box>
          <TextField label="提示文案（顯示於結帳頁）" size="small" fullWidth value={config.takeout?.notice || ''}
            onChange={(e) => setTakeout('notice', e.target.value)} placeholder="大量訂購（10 份以上）請來電確認" />
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.5, lineHeight: 1.7 }}>
            取餐時段自動依「營業時段」與公休/特殊日期產生（15 分鐘級距），扣除最短備餐時間，不需另外設定。<br />
            「需預訂」品項可於「菜單管理 → 品項編輯」設定需提前預訂天數（系統自動限制客人可選取餐日）；「時價」品項不會出現在外帶菜單。
          </Typography>
        </SectionPaper>

        {/* footer — 儲存列。手機用 position:fixed 釘在視窗底（sticky 在字級放大時因 zoom wrapper
            的 overflowX:auto 變捲動容器而失效 → 只黏到內容底；fixed 不受影響，實測大/特大字級皆貼齊）。
            桌機維持 sticky（在容器內、標準字級正常）。*/}
        <Box sx={{
          position: { xs: 'fixed', sm: 'sticky' }, bottom: 0,
          left: { xs: 0, sm: 'auto' }, right: { xs: 0, sm: 'auto' },
          zIndex: 1200, mt: { xs: 0, sm: 2 },
          bgcolor: 'background.paper',
          borderTop: '1px solid', borderColor: 'divider',
          borderLeft: { sm: '1px solid' }, borderRight: { sm: '1px solid' }, borderBottom: { sm: '1px solid' },
          borderRadius: { xs: 0, sm: 2 },
          boxShadow: '0 -2px 12px rgba(16,24,40,0.10)',
          px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5
        }}>
          {dirty
            ? <Chip size="small" label="● 有未儲存的變更" sx={{ fontWeight: 700, bgcolor: '#fff4d6', color: '#9a6a00' }} />
            : <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>設定已是最新</Typography>}
          <Stack direction="row" spacing={1.5}>
            <Button variant="outlined" onClick={handleReset} disabled={saving || !dirty}>重設</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || slugBlocked || hoursBlocked || !dirty}>
              {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '儲存設定'}
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* 新增餐廳照片 picker：從菜單選圖 / 上傳・貼網址 */}
      <Dialog open={photoPicker} onClose={() => setPhotoPicker(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 800 }}>新增餐廳照片</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button fullWidth variant={pickerTab === 'menu' ? 'contained' : 'outlined'} onClick={() => setPickerTab('menu')}>從菜單選圖</Button>
            <Button fullWidth variant={pickerTab === 'url' ? 'contained' : 'outlined'} onClick={() => setPickerTab('url')}>上傳／貼網址</Button>
          </Stack>

          {pickerTab === 'menu' ? (
            menuLoading ? (
              <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress size={28} /></Box>
            ) : menuPhotos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: 'text.disabled' }}>
                <Typography sx={{ fontSize: 14 }}>菜單還沒有照片 — 可改用「上傳／貼網址」</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.2 }}>
                {menuPhotos.map((m, i) => {
                  const used = coverList.includes(m.imageUrl);
                  return (
                    <Box key={i} onClick={() => !used && pickFromMenu(m.imageUrl)} sx={{ cursor: used ? 'default' : 'pointer' }}>
                      <Box sx={{ height: 84, borderRadius: 1.5, bgcolor: '#e5e7eb', backgroundImage: `url(${m.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid', borderColor: 'divider', position: 'relative' }}>
                        {used && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 700, bgcolor: 'rgba(0,0,0,.4)' }}>已加入</Box>}
                      </Box>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>{m.name}</Typography>
                    </Box>
                  );
                })}
              </Box>
            )
          ) : (
            <Stack spacing={2}>
              <Button component="label" variant="contained" startIcon={uploading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PhotoCameraIcon />} disabled={uploading}>
                上傳／拍照
                <input hidden type="file" accept="image/*" onChange={onPickCover} />
              </Button>
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>或直接貼圖片網址</Typography>
                <Stack direction="row" spacing={1}>
                  <TextField size="small" fullWidth placeholder="https://…" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} />
                  <Button variant="contained" onClick={addFromUrl} disabled={!urlInput.trim()}>加入</Button>
                </Stack>
              </Box>
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>上傳的照片也會存進圖庫,下次可直接選。</Typography>
            </Stack>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setPhotoPicker(false)}>關閉</Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Layout>
  );
};

export default WebBookingSettingsPage;
